import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { Button, I } from '@leanin/ui';
import { groupJoinLinkUrl } from '../../admin/referral-link-url';
import { useEnsureGroupJoinLink } from '../../../lib/mutations/groups';

// The whole leader surface for group join links: one click ensures the
// group's canonical /join/<slug> link (slug derived server-side from the
// group name) and puts the URL on the clipboard. Mirrors CopyLinkRow's
// swap-to-check feedback; a failed ensure or clipboard write shows a
// transient retry label instead of an error surface.
export function CopyJoinLinkButton({ groupId }: { readonly groupId: string }) {
  const t = useTranslations('Group');
  const ensure = useEnsureGroupJoinLink(groupId);
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  useEffect(() => {
    if (state === 'idle') return;
    const timer = window.setTimeout(() => setState('idle'), 1500);
    return () => window.clearTimeout(timer);
  }, [state]);
  const onCopy = () => {
    ensure.mutate(undefined, {
      onSuccess: (link) => {
        if (navigator.clipboard === undefined) {
          setState('failed');
          return;
        }
        void navigator.clipboard.writeText(groupJoinLinkUrl(link.code)).then(
          () => setState('copied'),
          () => setState('failed'),
        );
      },
      onError: () => setState('failed'),
    });
  };
  return (
    <Button
      type="button"
      variant="secondary"
      size="md"
      icon={state === 'copied' ? I.check : I.link}
      onClick={onCopy}
      disabled={ensure.isPending}
    >
      {state === 'copied'
        ? t('admin.joinLink.copied')
        : state === 'failed'
          ? t('admin.joinLink.failed')
          : t('admin.joinLink.copy')}
    </Button>
  );
}
